import React, { useState, useEffect, useRef } from 'react';
import axios from '../../../axiosConfig';
import styles from '../MicroProbe/Companies/CustomDropdownFinancialData.module.css';

const CompanySearchDropdown = ({ onSelect, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualTicker, setManualTicker] = useState('');
  const [manualCompanyName, setManualCompanyName] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadSuggestions = async () => {
      if (query.length > 1) {
        setLoading(true);
        try {
          const response = await axios.get(`/api/search/?q=${query}`);
          setSuggestions(response.data);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          setSuggestions([]);
        } finally {
          setLoading(false);
        }
      } else {
        setSuggestions([]);
      }
    };

    const timeoutId = setTimeout(loadSuggestions, 300); // Debounce
    return () => clearTimeout(timeoutId);
  }, [query]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleCompanySelect = (company) => {
    const companyData = {
      name: company.name,
      code: company.symbol || company.ticker,
      type: 'company'
    };
    onSelect(companyData.code, companyData);
    setIsOpen(false);
    setQuery('');
    setSuggestions([]);
  };

  const handleManualTickerSubmit = () => {
    if (manualTicker.trim()) {
      const companyData = {
        name: manualCompanyName.trim() || `Ticker: ${manualTicker.trim().toUpperCase()}`,
        code: manualTicker.trim().toUpperCase(),
        type: 'company'
      };
      onSelect(companyData.code, companyData);
      setManualTicker('');
      setManualCompanyName('');
      setShowManualInput(false);
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className={styles.customDropdown} ref={dropdownRef}>
      <div 
        className={styles.selected} 
        onClick={toggleDropdown}
        style={{ backgroundColor: '#cce7f0' }}
      >
        <span>{label}</span>
        <span className={`${styles.arrow} ${isOpen ? styles.open : ''}`} />
      </div>
      {isOpen && (
        <div className={styles.dropdownMenu} style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
            <input
              type="text"
              placeholder="Search company name..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                marginBottom: '10px',
                fontSize: '15px'
              }}
            />
            <button
              onClick={() => setShowManualInput(!showManualInput)}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                textAlign: 'left'
              }}
            >
              {showManualInput ? 'Hide Manual Entry' : 'Or enter ticker manually'}
            </button>
            {showManualInput && (
              <div style={{ marginTop: '10px' }}>
                <input
                  type="text"
                  placeholder="Enter company name (optional)"
                  value={manualCompanyName}
                  onChange={(e) => setManualCompanyName(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '5px',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  placeholder="Enter ticker (e.g., AAPL)"
                  value={manualTicker}
                  onChange={(e) => setManualTicker(e.target.value.toUpperCase())}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    marginBottom: '5px'
                  }}
                />
                <button
                  onClick={handleManualTickerSubmit}
                  style={{
                    width: '100%',
                    padding: '8px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Add Company
                </button>
              </div>
            )}
          </div>
          
          {loading && (
            <div style={{ padding: '10px', textAlign: 'center' }}>
              Loading...
            </div>
          )}
          
          {!loading && suggestions.length > 0 && (
            <div>
              {suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.id || index}
                  className={styles.dropdownOption}
                  onClick={() => handleCompanySelect(suggestion)}
                  style={{ 
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ fontWeight: 'bold' }}>{suggestion.name}</div>
                  {suggestion.symbol && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      Ticker: {suggestion.symbol}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {!loading && query.length > 1 && suggestions.length === 0 && (
            <div style={{ padding: '10px', textAlign: 'center', color: '#666' }}>
              No companies found. Try manual ticker entry.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CompanySearchDropdown;
